import React, { useEffect, useState } from 'react';
import { Box, Collapse, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import DatabaseIcon from '@mui/icons-material/Storage';
import OutputIcon from '@mui/icons-material/Save';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import MoonIcon from "@mui/icons-material/DarkMode";
import SunIcon from "@mui/icons-material/LightMode";
import PrintIcon from "@mui/icons-material/Print";
import AlignIcon from "@mui/icons-material/GridOn";
import AccountIcon from "@mui/icons-material/AccountBoxRounded";
import { Mesh, Color } from 'three';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js';
import logo from '../../assets/logo.png';
import Divider from '@mui/material/Divider';
import { SaveSettings, scheduleAutosave } from 'components/Viewer3d/SaveManager';
import { useAuth } from 'context/AuthContext';
import { useTheme } from 'context/ThemeContext';
import LoadTemplatePanel from 'components/Modals/LoadTemplateModal/LoadTemplatePanel';
import SettingsPanel from 'components/Modals/SettingsModal/SettingsPanel';
import { useApi } from 'context/ApiProvider';
import { PaginatedSearchRequestDto } from 'models/PaginatedSearchRequestDto';
import { fetchUserDesigns, getNewDesign } from 'services/designService';
import { formatLocalDate } from 'Utils/StringUtils';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { LOCAL_STORAGE_KEYS } from 'components/Viewer3d/consts';
import { useViewerStore } from 'context/ViewerContext';
import DatabasePanel from 'components/Modals/DatabaseModal/DatabasePanel';
import { useDesignStore } from 'context/DesignContext';
import { exportSTL, saveGCodeToFile } from 'Utils/ViewerUtils';
import toast from 'react-hot-toast';
import { backendInsoleTypeMap, InsoleType } from 'models/Insole';
import PatientDetailPanel from 'components/Modals/PatientModal/PatientDetailPanel';
import SaveTemplatePanel from 'components/Modals/SaveTemplateModal/SaveTemplatePanel';
import { HighHeelIcon, ShoeIcon, SlipperIcon } from 'components/Iconify/Iconify';
import { prepareGCode } from 'services/GCodeService';
import { DesignCategory } from 'models/InsoleDesign';
import { canUseDevelopmentExports, canUseDevelopmentUi } from 'auth/permissions';


interface MobileDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

const MobileDrawer:React.FC<MobileDrawerProps> = ({isOpen, onClose}) => {
    
    const { logout, user, isExhibition, updateExhibitionDesign } = useAuth();
    const canUseDevTools = canUseDevelopmentUi(user, isExhibition);
    const canUseDevExports = canUseDevelopmentExports(user);
    const api = useApi();
    const { isDarkMode, toggleTheme } = useTheme();
    const insoleDesign = useDesignStore((state) => state.insoleDesign);
    const hasPressure = useSelectedDesignStore((state) => state.hasPressure)

    const [isLoadTemplatePanelOpen, setLoadTemplatePanelOpen] = useState(false);
    const [isSettingsOpen, setSettingsOpen] = React.useState(false);
    const [isPatientOpen, setPatientOpen] = React.useState(false);
    const [isSaveTemplatePanelOpen, setSaveTemplatePanelOpen] = useState(false);
    const [isDatabaseOpen, setDatabaseOpen] = useState(false);
    const [isSaveExpanded, setSaveExpanded] = useState(false);
    const [isCategoryExpanded, setCategoryExpanded] = useState(false);
    const [designData, setDesignData] = useState<ApiDesignData[]>([]);
    const designCategory = insoleDesign?.category ?? DesignCategory.Insole;
    
    useEffect(() => {
      const designRequestDto = new PaginatedSearchRequestDto();
          fetchUserDesigns(api, designRequestDto).then((res) => {
          if (!res) {
            console.error('Error fetching user designs: no response');
          }
          else if (!res.data || res.data.length < 1) {
            console.error('Error fetching user designs: empty data');
          } else {
            const mappedData: ApiDesignData[] = res.data.map((design: ApiDesignData) => ({
              ...design,
              createdAt: formatLocalDate(design.createdAt)
            }));
            setDesignData(mappedData);
          }
          }).catch(err => {
            console.error('Error fetching user designs:', err);
          });
    }, [isOpen]);
    
    const handleLogoutClick = () => {
        scheduleAutosave('Current design', {
            now: true
        } as Partial<SaveSettings>).then(() => {
            logout();
        });
        onClose();
    };

    const handleLoadTemplateClick = () => {
      setLoadTemplatePanelOpen(true);
      onClose();
    };
    
    const handleLoadTemplateClose = () => {
      setLoadTemplatePanelOpen(false);
    };

    
    const handleSettingsClick = () => {
      setSettingsOpen(true);
      onClose();
    };

    const handleSettingsClose = () => {
      setSettingsOpen(false);
    };

    const handleDatabaseClick = () => {
      setDatabaseOpen(true);
      onClose();
    };

    const handleAlignPressureClick = () => {
      const design = useDesignStore.getState().insoleDesign;
      if (!design) return;
      if (!design.pressureData && useDesignStore.getState().RondomImageData.some(img => img == undefined)) {
        toast.error('Your design has no pressure.');
        return;
      }
      if (useViewerStore.getState().alignPressure){
        useViewerStore.getState().setLiveMode(useViewerStore.getState().oldLiveMode);
      } else {
        useViewerStore.getState().setOldLiveMode(useViewerStore.getState().liveMode);
        useViewerStore.getState().setLiveMode(useViewerStore.getState().alignPressure);
      }
      useViewerStore.getState().toggleAlignPressure();
      onClose();
    }

    const handlePrintClick = async () => {   
      onClose();
      const oldLiveMode = useViewerStore.getState().liveMode;
      useViewerStore.getState().setLiveMode(false);
      await prepareGCode(api, user, insoleDesign);
      useViewerStore.getState().setLiveMode(oldLiveMode);
    };

    const handleDatabaseClose = () => {
      setDatabaseOpen(false);
    };
    
    const handleNewDesignClick = () => {
      if (designData.length === 0) createNewDesign();
      else {
        scheduleAutosave('Current design', {
          now: true
        } as Partial<SaveSettings>).then(() => {
          createNewDesign();
        });
      }
    }

    const handlePatientClick = () => {
      setPatientOpen(true);
      onClose();
    };

    const handlePatientClose = () => {
      setPatientOpen(false);
    }

    const handleSaveAsTemplateClick = () => {
      setSaveTemplatePanelOpen(true);
      onClose();
    };

    const handleSaveAsTemplateClose = () => {
      setSaveTemplatePanelOpen(false);
    }

    const createNewDesign = async () => {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN);
  
      try {
        const templateName = backendInsoleTypeMap[InsoleType.Standard];
        await getNewDesign(api, templateName).then((dId: number) => {
          if (dId < 1) return;
          useSelectedDesignStore.setState({ designId: dId });
          useSelectedDesignStore.setState({ designUpdated: true });
          useViewerStore.setState({ newDesign: true });
          useSelectedDesignStore.setState({ hasPressure: false });
          useSelectedDesignStore.setState({ hasRondomImage: false });
          let storageData = { designId: dId, designPageNumber: 1 };
          localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_OPENED_DESIGN, JSON.stringify(storageData));
          if (isExhibition) updateExhibitionDesign(dId);
          onClose();
        });
      } catch (error) {
        console.error("Error Opening new template design", error);
      }
    }

    const handleExportInsolesClick = () => {
      insoleDesign?.insoles.forEach((insole, index) => {
        if (insole.mainMesh) {
          exportSTL(insole.mainMesh, `${insoleDesign.name}_${index === 0 ? 'Right' : 'Left'}`);
        }
      });
      onClose();
    };

    const handleExportGCodeClick = () => {
      insoleDesign?.insoles.forEach((insole, index) => {
        if (insole.gcodeText) {
          saveGCodeToFile(insole.gcodeText, `${insoleDesign.name}_${index === 0 ? 'Right' : 'Left'}.gcode`);
        }
      });
      onClose();
    };

    const handleExportLowpolyClick = () => {
      insoleDesign?.insoles.forEach((insole, index) => {
        if (insole.lowpolyBufferGeometry) {
          const lowpolyMesh = new Mesh(insole.lowpolyBufferGeometry);
          exportSTL(lowpolyMesh, `${insoleDesign.name}_Lowpoly_${index === 0 ? 'Right' : 'Left'}`);
        }
      });
      onClose();
    };

    const handleExportGCodeObjectClick = () => {
      const gcodes = useDesignStore.getState().getGCode();
      if (!gcodes?.length) return;

      const exporter = new OBJExporter();
      gcodes.forEach((gcode, index) => {
        if (gcode) {
          const vertexColors: string[] = [];
          const color = new Color();

          gcode.traverse((child) => {
            if (!(child instanceof Mesh)) return;

            const positions = child.geometry.getAttribute('position');
            const colors = child.geometry.getAttribute('color');
            if (!positions) return;

            for (let vertexIndex = 0; vertexIndex < positions.count; vertexIndex++) {
              if (colors && vertexIndex < colors.count) {
                color.fromBufferAttribute(colors, vertexIndex).convertLinearToSRGB();
              } else {
                color.set(0xffffff);
              }
              vertexColors.push(`${color.r} ${color.g} ${color.b}`);
            }
          });

          let vertexIndex = 0;
          const obj = exporter.parse(gcode)
            .split('\n')
            .map((line) => line.startsWith('v ')
              ? `${line} ${vertexColors[vertexIndex++] ?? '1 1 1'}`
              : line)
            .join('\n');
          const designName = insoleDesign?.name ?? 'gcode';
          saveGCodeToFile(
            obj,
            `${designName}_GCode_${index === 0 ? 'Right' : 'Left'}.obj`,
            'text/plain'
          );
        }
      });
      onClose();
    };

    const getCategoryIcon = (category: DesignCategory) => {
      if (category === DesignCategory.HighHeel) return <HighHeelIcon />;
      if (category === DesignCategory.Sandal) return <SlipperIcon />;
      return <ShoeIcon />;
    };

    const getCategoryLabel = (category: DesignCategory) => {
      if (category === DesignCategory.HighHeel) return 'High Heel';
      return category;
    };

    const handleModelClick = (model: DesignCategory) => {
      const event = new CustomEvent('updateCategoryState', { detail: { state: model } });
      document.dispatchEvent(event);
      setCategoryExpanded(false);
      onClose();
    };

    return (
      <>
        <Drawer
          variant="temporary"
          open={isOpen}
          onClose={onClose}
          sx={{ 
            '& .MuiDrawer-paper': { 
              width: 240,
              backgroundColor: 'var(--drawer-background)',
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pl: 2,
              pr: 1,
              py: 2,
            }}
          >
          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{ height: 40 }}
          />

          {/* Close Button */}
          <IconButton data-tour-id="m-drawer-close" onClick={onClose}>
            <CloseIcon sx={{color: 'var(--accent-color)'}}/>
          </IconButton>
        </Box>
          <List dense>
            <ListItemButton data-tour-id="m-new-design" onClick={handleNewDesignClick}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <AddIcon />
              </ListItemIcon>
              <ListItemText primary="New Design" sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
            <ListItemButton data-tour-id="m-database" onClick={handleDatabaseClick}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <DatabaseIcon />
              </ListItemIcon>
              <ListItemText primary="Database" sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
            <ListItemButton data-tour-id="m-template" onClick={handleLoadTemplateClick}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <FileOpenIcon />
              </ListItemIcon>
              <ListItemText primary="Template" sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
            <ListItemButton data-tour-id="m-patient" onClick={handlePatientClick}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <AccountIcon />
              </ListItemIcon>
              <ListItemText primary="Patient" sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
            { hasPressure && 
              <ListItemButton data-tour-id="m-align" onClick={handleAlignPressureClick}>
                <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                  <AlignIcon />
                </ListItemIcon>
                <ListItemText primary="Align Pressure" sx={{ color: 'var(--toolbar-label-text)' }}/>
              </ListItemButton>
            }
            {canUseDevTools && (
              <>
                <ListItemButton data-tour-id="m-model-category" onClick={() => setCategoryExpanded(!isCategoryExpanded)}>
                  <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                    {getCategoryIcon(designCategory)}
                  </ListItemIcon>
                  <ListItemText primary={getCategoryLabel(designCategory)} sx={{ color: 'var(--toolbar-label-text)' }}/>
                  {isCategoryExpanded
                    ? <ExpandLess sx={{ color: 'var(--toolbar-label-text)' }} />
                    : <ExpandMore sx={{ color: 'var(--toolbar-label-text)' }} />}
                </ListItemButton>
                <Collapse in={isCategoryExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    <ListItemButton sx={{ pl: 6 }} onClick={() => handleModelClick(DesignCategory.Insole)}>
                      <ListItemText primary="Insole" sx={{ color: 'var(--toolbar-label-text)' }}/>
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={() => handleModelClick(DesignCategory.Sandal)}>
                      <ListItemText primary="Sandal" sx={{ color: 'var(--toolbar-label-text)' }}/>
                    </ListItemButton>
                    <ListItemButton sx={{ pl: 6 }} onClick={() => handleModelClick(DesignCategory.HighHeel)}>
                      <ListItemText primary="High Heel" sx={{ color: 'var(--toolbar-label-text)' }}/>
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}
            <ListItemButton data-tour-id="m-print" onClick={handlePrintClick}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <PrintIcon />
              </ListItemIcon>
              <ListItemText primary="Print" sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
            <ListItemButton data-tour-id="m-settings" onClick={handleSettingsClick}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
            <ListItemButton data-tour-id="m-theme" onClick={toggleTheme}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                { isDarkMode ? <SunIcon /> : <MoonIcon />}
              </ListItemIcon>
              <ListItemText primary={ isDarkMode ? 'Light Theme' : 'Dark Theme'} sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
            <Divider sx={{mx:1}}/>
            <ListItemButton data-tour-id="m-save" onClick={() => setSaveExpanded(!isSaveExpanded)}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <OutputIcon />
              </ListItemIcon>
              <ListItemText primary="Save" sx={{ color: 'var(--toolbar-label-text)' }}/>
              {isSaveExpanded
                ? <ExpandLess sx={{ color: 'var(--toolbar-label-text)' }} />
                : <ExpandMore sx={{ color: 'var(--toolbar-label-text)' }} />}
            </ListItemButton>
            <Collapse in={isSaveExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                <ListItemButton sx={{ pl: 6 }} onClick={handleExportInsolesClick}>
                  <ListItemText primary="Export Insoles" sx={{ color: 'var(--toolbar-label-text)' }}/>
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 6 }}
                  onClick={handleExportGCodeClick}
                  disabled={(useDesignStore.getState().gCodes?.length ?? 0) < 1}
                >
                  <ListItemText primary="Export G-Code" sx={{ color: 'var(--toolbar-label-text)' }}/>
                </ListItemButton>
                <ListItemButton sx={{ pl: 6 }} onClick={handleSaveAsTemplateClick}>
                  <ListItemText primary="Save as Template" sx={{ color: 'var(--toolbar-label-text)' }}/>
                </ListItemButton>
                {canUseDevExports && (
                  <>
                    <ListItemButton sx={{ pl: 6 }} onClick={handleExportLowpolyClick}>
                      <ListItemText primary="Export Lowpoly" sx={{ color: 'var(--toolbar-label-text)' }}/>
                    </ListItemButton>
                    <ListItemButton
                      sx={{ pl: 6 }}
                      onClick={handleExportGCodeObjectClick}
                      disabled={(useDesignStore.getState().gCodes?.length ?? 0) < 1}
                    >
                      <ListItemText primary="Export G-Code Object" sx={{ color: 'var(--toolbar-label-text)' }}/>
                    </ListItemButton>
                  </>
                )}
              </List>
            </Collapse>
            <Divider sx={{mx:1}}/>
            <ListItemButton onClick={handleLogoutClick}>
              <ListItemIcon sx={{color: 'var(--toolbar-button-border)', minWidth: '34px'}}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" sx={{ color: 'var(--toolbar-label-text)' }}/>
            </ListItemButton>
          </List>
        </Drawer>
        {!user?.isAdmin && <DatabasePanel isOpen={isDatabaseOpen} onCloseCallback={handleDatabaseClose} />}
        <LoadTemplatePanel isOpen={isLoadTemplatePanelOpen} onClose={handleLoadTemplateClose} />
        <SettingsPanel isOpen={isSettingsOpen} onClose={handleSettingsClose} />
        <PatientDetailPanel isOpen={isPatientOpen} onClose={handlePatientClose} />
        <SaveTemplatePanel isOpen={isSaveTemplatePanelOpen} onClose={handleSaveAsTemplateClose} />
      </>
    );
}

export default MobileDrawer;
